import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  apiAuthMe,
  apiRegisterBusiness,
  apiRegisterBusinessProfile,
} from "../lib/api";
import * as API from "../lib/api"; // for optional apiCountriesIndex presence
import { useBranding } from "../lib/branding";

type Step = "account" | "profile";

type Country = {
  name: string;
  iso?: string;
  dial_code?: string;
  emoji?: string;
};

function Banner({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 p-3 text-sm">
      {children}
    </div>
  );
}

/** Load countries from facade (preferred), then /xapi, then /api, then window fallback */
function useCountries() {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        const fn: any = (API as any).apiCountriesIndex;
        if (typeof fn === "function") {
          const res = await fn();
          if (!alive) return;
          const list: Country[] = res?.countries ?? res?.data ?? res ?? [];
          if (Array.isArray(list) && list.length) {
            setCountries(list);
            return;
          }
        }

        const tryFetch = async (url: string) => {
          const r = await fetch(url, { credentials: "include" });
          if (!r.ok) return null;
          const j = await r.json();
          const arr: Country[] = j?.countries ?? j?.data ?? j ?? [];
          return Array.isArray(arr) ? arr : null;
        };

        let list =
          (await tryFetch("/xapi/countries")) ??
          (await tryFetch("/api/countries"));

        if (!list || !list.length) {
          const winAny = window as any;
          if (Array.isArray(winAny?.__COUNTRIES__) && winAny.__COUNTRIES__.length) {
            list = winAny.__COUNTRIES__;
          }
        }

        if (!alive) return;
        setCountries(Array.isArray(list) ? list : []);
      } catch {
        if (!alive) return;
        setCountries([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  return { countries, loading };
}

export default function RegisterBusiness() {
  const branding = useBranding();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>("account");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Step 1 (account)
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [usernameHint, setUsernameHint] = useState<string>("");

  // Step 2 (business profile)
  const [companyName, setCompanyName] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [address, setAddress] = useState("");
  const [postal, setPostal] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  // Phone (parity with personal)
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Redirect guard
  const [redirecting, setRedirecting] = useState(false);
  const redirectedOnce = useRef(false);

  // Countries (fixed)
  const { countries, loading: loadingCountries } = useCountries();

  // Auto-redirect if server already considers profile complete, but ONLY
  // when we are NOT explicitly on ?step=profile to avoid loops.
  useEffect(() => {
    let alive = true;
    const wantProfile = params.get("step") === "profile";
    if (wantProfile) return; // do not probe on the profile step

    fetch("/xapi/register/business/profile", {
      credentials: "include",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || redirectedOnce.current || !j) return;
        if (j.redirect && window.location.pathname !== j.redirect) {
          redirectedOnce.current = true;
          nav(String(j.redirect).replace(/^\/app/, ""));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [nav, params]);

  // If authenticated but not explicitly sent to profile, go /app/dashboard
  useEffect(() => {
    let alive = true;
    apiAuthMe()
      .then((me) => {
        if (!alive || redirectedOnce.current) return;
        if (me?.user) {
          const wantProfile = params.get("step") === "profile";
          if (!wantProfile) {
            redirectedOnce.current = true;
            nav("/dashboard");
          }
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [nav, params]);

  // Honor ?step=profile
  useEffect(() => {
    if (params.get("step") === "profile") setStep("profile");
  }, [params]);

  const inputCls = useMemo(
    () =>
      "mt-1 block w-full rounded-xl border px-4 py-3 text-base " +
      "border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100",
    []
  );

  function setServerErrors(e: any) {
    const errs = (e?.data?.errors as Record<string, string[]>) || {};
    setFieldErrors(errs);
  }

  function alphaOnly(u: string) {
    return /^[A-Za-z]+$/.test(u);
  }

  async function handleUsernameBlur() {
    setUsernameHint("");
    const u = username.trim();
    if (!u) return;
    if (!alphaOnly(u)) {
      setUsernameHint("Username can only contain letters a–z.");
      return;
    }
    try {
      const res = await fetch(`/check-username?username=${encodeURIComponent(u)}`, {
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      const data = await res.json();
      if (!data?.available) setUsernameHint("Username is already taken.");
    } catch {
      // ignore soft error
    }
  }

  async function onCreateAccount(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      if (!alphaOnly(username)) {
        throw Object.assign(new Error("Invalid username"), {
          data: { errors: { username: ["Username can only contain letters a–z."] } },
        });
      }
      if (!password || !password2 || password !== password2) {
        const msg = !password || !password2
          ? "Password and confirmation are required."
          : "Passwords do not match.";
        throw Object.assign(new Error(msg), { data: { errors: { password: [msg] } } });
      }

      await apiRegisterBusiness({
        username: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        password_confirmation: password2,
        customer_type: "business",
      });

      // After register() the server rotated session + CSRF. Refresh immediately.
      try {
        await fetch("/xapi/csrf", { credentials: "include" });
      } catch { /* soft */ }

      setStep("profile");
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Registration failed");
      setServerErrors(e);
    } finally {
      setLoading(false);
    }
  }

  async function onSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    if (redirecting) return;
    setError(null);
    setFieldErrors({});
    setLoading(true);
    setRedirecting(true);
    try {
      const res = await apiRegisterBusinessProfile({
        company_name:   companyName.trim(),
        company_number: companyNumber.trim(),
        vat_number:     vatNumber.trim(),
        address:        address.trim(),
        postal_code:    postal.trim(),
        city:           city.trim(),
        country:        country.trim(),
        phone_country_code: phoneCode.trim(),
        phone_number:       phoneNumber.trim(),
      });

      // Single source of truth: follow server redirect or go to dashboard
      const dest = res?.redirect?.replace(/^\/app/, "") ?? "/dashboard";
      redirectedOnce.current = true;
      nav(dest);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || "Could not save profile");
      setServerErrors(e);
      setRedirecting(false);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
      <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow p-6">
        <div className="text-center mb-4">
          <img
            src={branding.theme.logo_square_url}
            alt={branding.product_name}
            className="mx-auto mb-3 h-14 w-14 object-contain"
          />
        </div>

        <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-100 text-center mb-4">
          {step === "account" ? "Create your business profile" : "Business profile"}
        </h1>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
            {error}
          </div>
        )}

        {step === "account" ? (
          <form className="space-y-5" onSubmit={onCreateAccount}>
            <div>
              <label className="block text-sm font-medium">Username (only letters)</label>
              <input
                className={inputCls}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onBlur={handleUsernameBlur}
                autoComplete="username"
                required
              />
              {usernameHint && <p className="text-xs text-red-600 mt-1">{usernameHint}</p>}
              {fieldErrors.username?.length ? (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.username[0]}</p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              {fieldErrors.email?.length ? (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.email[0]}</p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">Password</label>
                <input
                  type="password"
                  className={inputCls}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Confirm Password</label>
                <input
                  type="password"
                  className={inputCls}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>
            {fieldErrors.password?.length ? (
              <p className="text-xs text-red-600 -mt-3">{fieldErrors.password[0]}</p>
            ) : null}

            <button type="submit" disabled={loading} className="btn w-full mt-1">
              {loading ? "Creating…" : "Create account"}
            </button>

            <div className="text-center text-sm text-gray-600 dark:text-gray-300">
              Already have an account?{" "}
              <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
            </div>
          </form>
        ) : (
          <form className="space-y-5" onSubmit={onSaveProfile}>
            <Banner>
              Your business login is created. Please complete all your company information below to continue.
            </Banner>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">Company Name</label>
                <input
                  className={inputCls}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
                {fieldErrors.company_name?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.company_name[0]}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">Company Registration Number</label>
                <input
                  className={inputCls}
                  value={companyNumber}
                  onChange={(e) => setCompanyNumber(e.target.value)}
                  required
                />
                {fieldErrors.company_number?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.company_number[0]}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium">VAT Number</label>
                <input
                  className={inputCls}
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  required
                />
                {fieldErrors.vat_number?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.vat_number[0]}</p>
                )}
              </div>

              {/* Mobile phone */}
              <div>
                <label className="block text-sm font-medium">Phone number</label>
                <div className="flex gap-2">
                  <input
                    placeholder="Country code"
                    className={inputCls + " w-28"}
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    required
                  />
                  <input
                    placeholder="Phone number"
                    className={inputCls}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>
                {fieldErrors.phone_country_code?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.phone_country_code[0]}</p>
                )}
                {fieldErrors.phone_number?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.phone_number[0]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium">Postal Code</label>
                <input
                  className={inputCls}
                  value={postal}
                  onChange={(e) => setPostal(e.target.value)}
                  required
                />
                {fieldErrors.postal_code?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.postal_code[0]}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Address</label>
                <input
                  className={inputCls}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                />
                {fieldErrors.address?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.address[0]}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">City</label>
                <input
                  className={inputCls}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
                {fieldErrors.city?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.city[0]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    {loadingCountries ? "Loading…" : "Select a country"}
                  </option>
                  {countries.map((c) => (
                    <option key={c.iso || c.name} value={c.name}>
                      {c.emoji ? `${c.emoji} ` : ""}
                      {c.name}
                    </option>
                  ))}
                </select>
                {fieldErrors.country?.length && (
                  <p className="text-xs text-red-600 mt-1">{fieldErrors.country[0]}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStep("account")}
                className="btn-white"
              >
                Back
              </button>
              <button type="submit" disabled={loading || redirecting} className="btn">
                {loading ? "Saving…" : "Save & Continue"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
